/**
 * リアルタイム対戦ドメインロジック（T15）。
 *
 * 責務:
 *   - プレイヤー色の決定（ホスト=先手=black / ゲスト=後手=white という単純ルール）
 *   - BattleSnapshot の構築と着手適用
 *   - 勝敗判定（engine.ts の純粋関数を再利用）
 *
 * ゲームエンジン（`src/game/engine.ts`）と AI モジュールは本タスクで変更しない。
 * 対戦の状態は engine の純粋関数に完全に従属するため、両クライアントが同じ
 * 着手シーケンスを受け取れば同じ BattleSnapshot に収束する（決定論同期）。
 */

import {
  checkWin,
  isBoardFull,
  opponent,
  placeStone,
  computeScore,
} from '../game/engine'
import { createEmptyBoard, type Cell } from '../game/types'
import type {
  BattleMember,
  BattleSnapshot,
} from '../config/realtime'
import type { RoomInfo } from '../config/multiplayer'

// ---------------------------------------------------------------------------
// 初期化
// ---------------------------------------------------------------------------

/**
 * ルームとプレイヤーの userId から、両プレイヤーの色（先手/後手）を決定する。
 * 「ホスト=black（先手）、ゲスト=white（後手+10%コミ）」という単純ルール。
 *
 * 先手後手のランダム化は未実装。ホストが常に先手なので、ランダム性を入れる場合は
 * 将来の拡張として StartMatch コマンドの payload に color を載せる設計にする。
 *
 * @param room ルーム情報（ホスト ID を含む）
 * @param selfUserId 自分の userId（プロフィール経由で取得済みのもの）
 * @param selfProfile 自分の displayName / avatar
 * @param opponentProfile 相手の displayName / avatar（未取得なら null）
 */
export function buildInitialBattle(
  room: RoomInfo,
  selfUserId: string,
  selfProfile: { displayName: string; avatarUrl: string | null },
  opponentProfile: { userId: string; displayName: string; avatarUrl: string | null } | null,
): BattleSnapshot {
  const selfColor: 'black' | 'white' = room.hostUserId === selfUserId ? 'black' : 'white'
  const oppColor = opponent(selfColor)

  const selfMember: BattleMember = {
    userId: selfUserId,
    color: selfColor,
    displayName: selfProfile.displayName,
    avatarUrl: selfProfile.avatarUrl,
    online: true,
  }

  const members: BattleMember[] = [selfMember]
  if (opponentProfile) {
    members.push({
      userId: opponentProfile.userId,
      color: oppColor,
      displayName: opponentProfile.displayName,
      avatarUrl: opponentProfile.avatarUrl,
      online: true,
    })
  }

  return {
    board: createEmptyBoard(),
    turn: 'black',
    turnStartedAt: Date.now(),
    turnCount: 0,
    gameOver: false,
    winner: null,
    members,
    lastMove: null,
  }
}

// ---------------------------------------------------------------------------
// 着手の適用
// ---------------------------------------------------------------------------

/**
 * PlaceStone コマンドを現在の BattleSnapshot に適用する。
 *
 * 戻り値:
 *   - null: 無効手（既に石がある / 手番違反 / ゲーム終了済み）
 *   - BattleSnapshot: 適用後の状態
 *
 * 着手者が「想定されるプレイヤー」と一致しない場合も null を返す（不正防止）。
 */
export function applyPlaceStone(
  prev: BattleSnapshot,
  row: number,
  col: number,
  color: 'black' | 'white',
): BattleSnapshot | null {
  if (prev.gameOver) return null
  if (prev.turn !== color) return null
  if (row < 0 || col < 0 || row >= prev.board.length || col >= prev.board.length) return null
  if (prev.board[row][col] !== 'empty') return null

  const nextBoard = placeStone(prev.board, row, col, color)

  // 勝利判定
  if (checkWin(nextBoard, color)) {
    return {
      ...prev,
      board: nextBoard,
      turn: opponent(color),
      turnStartedAt: Date.now(),
      turnCount: prev.turnCount + 1,
      gameOver: true,
      winner: color,
      lastMove: { row, col, color },
    }
  }

  // 盤面満杯 → スコア比較
  if (isBoardFull(nextBoard)) {
    const blackTotal = computeScore(nextBoard, 'black').total
    const whiteTotal = computeScore(nextBoard, 'white').total
    const winner: 'black' | 'white' | 'draw' =
      blackTotal > whiteTotal ? 'black' : whiteTotal > blackTotal ? 'white' : 'draw'
    return {
      ...prev,
      board: nextBoard,
      turn: opponent(color),
      turnStartedAt: Date.now(),
      turnCount: prev.turnCount + 1,
      gameOver: true,
      winner,
      lastMove: { row, col, color },
    }
  }

  // 通常進行
  return {
    ...prev,
    board: nextBoard,
    turn: opponent(color),
    turnStartedAt: Date.now(),
    turnCount: prev.turnCount + 1,
    gameOver: false,
    winner: null,
    lastMove: { row, col, color },
  }
}

/**
 * 投了を適用する。forfeiter は勝者にならない。
 */
export function applyForfeit(
  prev: BattleSnapshot,
  forfeiterColor: 'black' | 'white',
): BattleSnapshot {
  return {
    ...prev,
    gameOver: true,
    winner: opponent(forfeiterColor),
  }
}

// ---------------------------------------------------------------------------
// Snapshot serialization（StateSnapshot コマンドで送受信するための JSON 対応）
// ---------------------------------------------------------------------------

export interface SerializedBattleSnapshot {
  board: Cell[][]
  turn: 'black' | 'white'
  turnCount: number
  gameOver: boolean
  winner: 'black' | 'white' | 'draw' | null
  lastMove: { row: number; col: number; color: 'black' | 'white' } | null
}

export function serializeSnapshot(snap: BattleSnapshot): SerializedBattleSnapshot {
  return {
    board: snap.board.map((row) => [...row]),
    turn: snap.turn,
    turnCount: snap.turnCount,
    gameOver: snap.gameOver,
    winner: snap.winner,
    lastMove: snap.lastMove,
  }
}

/**
 * 受信した snapshot を現在の members 情報とマージして復元する。
 * members は実通信で変化しうるので、呼び出し側の最新値を使う。
 */
export function restoreSnapshot(
  serialized: SerializedBattleSnapshot,
  members: BattleMember[],
): BattleSnapshot {
  return {
    board: serialized.board.map((row) => [...row]),
    turn: serialized.turn,
    turnStartedAt: Date.now(),
    turnCount: serialized.turnCount,
    gameOver: serialized.gameOver,
    winner: serialized.winner,
    members,
    lastMove: serialized.lastMove,
  }
}
